import { Avatar } from 'antd';
import styled, {
  css,
  FlattenSimpleInterpolation,
  keyframes
} from 'styled-components';
import type { TSizeType } from 'interfaces';

export const SIZE_STYLES: Record<
  TSizeType,
  Record<
    'wrapper' | 'avatar' | 'infoWrapper' | 'text' | 'secondaryText' | 'play',
    FlattenSimpleInterpolation
  >
> = {
  huge: {
    wrapper: css`
      padding: 24px;
      gap: 16px;
      border-radius: 80px;
    `,
    avatar: css`
      width: 84px;
      height: 84px;
      margin-left: 12px;
    `,
    infoWrapper: css`
      gap: 8px;
    `,
    text: css`
      font-weight: 400;
      font-size: 24px;
      line-height: 28px;
    `,
    secondaryText: css`
      font-weight: 700;
      font-size: 28px;
      line-height: 32px;
    `,
    play: css`
      margin-left: 64px;
      & > div {
        padding: 28px;
        font-size: 22px;
        line-height: 24px;
      }
    `
  },
  large: {
    wrapper: css`
      padding: 20px;
      gap: 14px;
      border-radius: 80px;
    `,
    avatar: css`
      width: 68px;
      height: 68px;
      margin-left: 10px;
    `,
    infoWrapper: css`
      gap: 6px;
    `,
    text: css`
      font-weight: 400;
      font-size: 20px;
      line-height: 24px;
    `,
    secondaryText: css`
      font-weight: 700;
      font-size: 24px;
      line-height: 28px;
    `,
    play: css`
      margin-left: 40px;
      & > div {
        padding: 20px;
        font-size: 20px;
        line-height: 24px;
      }
    `
  },
  medium: {
    wrapper: css`
      padding: 20px;
      gap: 12px;
      border-radius: 80px;
    `,
    avatar: css`
      width: 60px;
      height: 60px;
      margin-left: 12px;
    `,
    infoWrapper: css`
      gap: 4px;
    `,
    text: css`
      font-weight: 400;
      font-size: 20px;
      line-height: 24px;
    `,
    secondaryText: css`
      font-weight: 700;
      font-size: 24px;
      line-height: 28px;
    `,
    play: css`
      margin-left: 20px;
      & > div {
        padding: 16px;
        font-size: 20px;
        line-height: 24px;
      }
    `
  },
  small: {
    wrapper: css`
      padding: 10px;
      gap: 8px;
      border-radius: 80px;
    `,
    avatar: css`
      width: 52px;
      height: 52px;
      margin-left: 0;
    `,
    infoWrapper: css`
      gap: 2px;
    `,
    text: css`
      font-weight: 400;
      font-size: 16px;
      line-height: 20px;
    `,
    secondaryText: css`
      font-weight: 700;
      font-size: 20px;
      line-height: 24px;
    `,
    play: css`
      margin-left: 0;
      & > div {
        padding: 12px;
        font-size: 18px;
        line-height: 20px;
      }
    `
  }
};

export const TerminalAccountAvatarWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  background: linear-gradient(
    270deg,
    var(--v3-primary-4) 0%,
    var(--v3-primary-5) 50%,
    var(--v3-primary-6) 100%
  );
`;

export const TerminalAccountUserAvatar = styled(Avatar)`
  border: 2px solid var(--v3-white);
`;

export const TerminalAccountUserInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  overflow: hidden;
`;

export const TerminalAccountUserName = styled.div`
  width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: var(--v3-text-color);
`;

export const TerminalAccountUserTickets = styled.div`
  width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: var(--v3-primary-color);
`;

const gradient = keyframes`
  50% {
    background-position: 140% 50%;
  }
`;

export const TerminalAccountPlayButton = styled.div`
  position: relative;
  background: linear-gradient(
    -90deg,
    #ee7752,
    #e73c7e,
    #23a6d5,
    #23d5ab,
    #ee7752
  );
  background-size: 400% 100%;
  border: none;
  border-radius: 80px;
  padding: 0;
  animation: ${gradient} 4s ease infinite;
  cursor: pointer;

  & > div {
    color: var(--v3-text-color-inverse);
    min-height: 24px;
    border-radius: inherit;
    background-size: inherit;
    background-image: inherit;
    animation: ${gradient} 4s ease infinite;
    position: relative;
    z-index: 1;
    mix-blend-mode: overlay;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: bold;
    white-space: nowrap;
  }
`;

export const TerminalAccountFixedContainer = styled.div`
  position: fixed;
  z-index: 10;
  bottom: 40px;
  left: 50%;
  transform: translate(-50%);
  max-width: 100vw;
`;

export const TerminalAccountWrapper = styled.div<{ $size: TSizeType }>`
  width: fit-content;
  max-width: 100%;
  background: var(--v3-black-0);
  box-shadow: 0 20px 30px rgba(212, 215, 224, 0.4);
  padding: 24px;
  border-radius: 80px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  flex-wrap: nowrap;
  gap: 16px;
  z-index: 2;

  ${({ $size }) => SIZE_STYLES[$size].wrapper}

  ${TerminalAccountAvatarWrapper} {
    ${({ $size }) => SIZE_STYLES[$size].avatar}
  }

  ${TerminalAccountUserInfo} {
    ${({ $size }) => SIZE_STYLES[$size].infoWrapper}
  }

  ${TerminalAccountUserName} {
    ${({ $size }) => SIZE_STYLES[$size].text}
  }

  ${TerminalAccountUserTickets} {
    ${({ $size }) => SIZE_STYLES[$size].secondaryText}
  }

  ${TerminalAccountPlayButton} {
    ${({ $size }) => SIZE_STYLES[$size].play}
  }
`;
